export type LivestreamSchedulePhase =
  | "none"
  | "ended"
  | "before-start-far"
  | "before-start-countdown"
  | "waiting-for-host"
  | "active";

const ONE_HOUR_MS = 60 * 60 * 1000;

function parseScheduleDate(value?: string | Date | null): Date | null {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (isNaN(date.getTime())) return null;
  return date;
}

export function getLivestreamSchedulePhase({
  start,
  end,
  now,
}: {
  start?: string | Date | null;
  end?: string | Date | null;
  now: Date;
}): LivestreamSchedulePhase {
  const startDate = parseScheduleDate(start);
  const endDate = parseScheduleDate(end);

  if (!startDate && !endDate) return "none";

  if (endDate && now > endDate) return "ended";

  if (startDate && now < startDate) {
    const msUntilStart = startDate.getTime() - now.getTime();
    if (msUntilStart >= ONE_HOUR_MS) return "before-start-far";
    return "before-start-countdown";
  }

  if (startDate || endDate) return "waiting-for-host";

  return "none";
}

export function shouldDeferLivestreamJoin({
  meetingFormat,
  isStaffJoinLink,
}: {
  meetingFormat: string;
  isStaffJoinLink: boolean;
  start?: string | Date | null;
  end?: string | Date | null;
  now?: Date;
}): boolean {
  if (meetingFormat !== "livestream" || isStaffJoinLink) return false;
  return false;
}

export function formatLivestreamStartSchedule({
  start,
}: {
  start: string | Date;
}): { date: string; time: string } | null {
  const date = parseScheduleDate(start);
  if (!date) return null;

  return {
    date: date.toLocaleString(undefined, {
      weekday: "long",
      month: "long",
      day: "numeric",
    }),
    time: date.toLocaleString(undefined, {
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
    }),
  };
}

export function formatLivestreamCountdown({
  target,
  now,
}: {
  target: string | Date;
  now: Date;
}): string {
  const targetDate = parseScheduleDate(target);
  if (!targetDate) return "00:00";

  const msRemaining = Math.max(0, targetDate.getTime() - now.getTime());
  const totalSeconds = Math.ceil(msRemaining / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }

  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}
